import { Button, Grid, GridItem, HStack } from "@chakra-ui/react";
import { Card, Link, Spinner } from "@components/ui-kit";
import {
  NotFoundOutgoingInvoice,
  NotFoundProforma,
} from "@components/widgets/ErrorPage";
import { Trans, t } from "@lingui/macro";
import { Icon } from "@src/components/ui-kit/Icon";
import { ModelLockBanner } from "@src/components/ui-kit/model-lock-banner";
import { NAV_HEIGHT } from "@src/components/widgets/AppSubNav/AppSubNav";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import React, { type FC, useEffect } from "react";
import { useInvoiceDetailStore } from "../store";
import { AttachedFilesCard } from "./AttachedFilesCard";
import { AttachedProformasCard } from "./AttachedProformasCard";
import { BudgetItemsCard } from "./BudgetItemsCard";
import { CardTitle } from "./common";
import { InvoiceEditButton } from "./edit-button";
import { InternalNoteCard } from "./InternalNoteCard";
import { MainCard } from "./MainCard";
import { PrepaymentCard } from "./PrepaymentCard";
import { ProjectsCard } from "./ProjectsCard";
import { PaymentsCard } from "./payments-card";
import { Summary } from "./Summary";
import { TimeBillingCard } from "./TimeBillingCard/time-billing-card";

interface ContainerProps {
  id: string;
}

export const Container: FC<React.PropsWithChildren<ContainerProps>> = observer(
  function Container({ id }) {
    const { sendInvoiceModalStore } = useStore();
    const store = useInvoiceDetailStore();

    useEffect(() => {
      if (!id) return;
      store.fetchInvoice(id);
    }, [id, store]);

    if (!store.invoice && store.isLoading) {
      return <Spinner absolute />;
    }

    if (store.isNotFound) {
      if (store.isProforma) {
        return <NotFoundProforma />;
      }
      return <NotFoundOutgoingInvoice />;
    }

    if (!store.invoice) {
      return null;
    }

    return (
      <React.Fragment>
        <ModelLockBanner
          mb="6"
          show={store.hasModelLock}
          loading={store.isUnlocking}
          onClick={store.unlock}
          canUnlock={can("outgoingInvoice_modelLock")}
          model={store.isProforma ? t`proforma invoice` : t`outgoing invoice`}
        />
        <Grid
          gap="5"
          templateColumns={{
            base: "",
            lg: "2fr 1fr",
          }}
        >
          <GridItem>
            <Grid gap="4">
              <MainCard />

              {store.isOutgoing && (
                <React.Fragment>
                  <BudgetItemsCard />
                  <PrepaymentCard />
                  <Summary />
                </React.Fragment>
              )}
            </Grid>
          </GridItem>

          <GridItem>
            <Grid
              gap="4"
              position="sticky"
              top={`calc(${NAV_HEIGHT}px + var(--chakra-space-4))`}
            >
              <HStack spacing="4" w="full">
                {can("outgoingInvoice_modelLock") && (
                  <Button
                    flex="1 1 100%"
                    colorScheme="orange"
                    isLoading={store.isLockToggling}
                    leftIcon={
                      store.hasModelLock ? (
                        <Icon name="lock-unlocked-01" />
                      ) : (
                        <Icon name="lock-01" />
                      )
                    }
                    onClick={() =>
                      store.hasModelLock ? store.unlock() : store.lock()
                    }
                    variant="outline"
                  >
                    {store.hasModelLock ? (
                      <Trans>Unlock</Trans>
                    ) : (
                      <Trans>Lock</Trans>
                    )}
                  </Button>
                )}

                {can("outgoingInvoice_sendToClient") && (
                  <Button
                    leftIcon={<Icon name="send-03" />}
                    flex="1 1 100%"
                    onClick={() => {
                      if (!store.invoice) return;
                      sendInvoiceModalStore.modalState.onOpen({
                        id: id,
                        number: store.invoice.invoice_no,
                        type: store.isOutgoing ? "outgoing" : "proforma",
                        onSubmit: () => {
                          sendInvoiceModalStore.modalState.onClose();
                        },
                      });
                    }}
                    variant="outline"
                  >
                    <Trans>Send</Trans>
                  </Button>
                )}
                <InvoiceEditButton />
              </HStack>
              <ProjectsCard />
              <TimeBillingCard />
              <PaymentsCard />
              {store.invoice.isInvoice && <AttachedProformasCard />}
              {store.invoice.isReconcilliationInvoice && (
                <Card>
                  <Card.Section>
                    <CardTitle>
                      <Trans>Attached payment receipts</Trans>
                    </CardTitle>
                    {store.invoice.proofOfPayments?.map((proof) => (
                      <Link
                        key={proof.invoice_no}
                        href={{
                          pathname: "/invoices/outgoing-invoices/detail",
                          query: {
                            id: proof.id,
                          },
                        }}
                      >
                        #{proof.invoice_no}
                      </Link>
                    ))}
                  </Card.Section>
                </Card>
              )}
              <AttachedFilesCard />
              <InternalNoteCard />
            </Grid>
          </GridItem>
        </Grid>
      </React.Fragment>
    );
  },
);
